import { must } from "@/common/utils/must";
import JssUtils from "@/common/JssParser";

type Jss = ReturnType<typeof JssUtils>;

// Old IE builds the sheet through `styleSheet.cssText`.
type LegacyStyleElement = HTMLStyleElement & {
  styleSheet?: { cssText: string };
};

export function createStyleElement(
  document: Document,
  id: string,
): LegacyStyleElement {
  // Create the <style> tag
  const style = document.createElement("style");
  style.id = id;
  return style;
}

export function removeStylesheet(document: Document, id: string): boolean {
  const style = document.getElementById(id);
  if (style && style.tagName.toLowerCase() === "style") {
    must(style.parentNode, "stylesheet parent").removeChild(style);
    return true;
  }
  return false;
}

// links is an array of ids for the link elements
export function removeStylesheetLinks(document: Document, links: string[]) {
  for (let i = 0, max = links.length; i < max; i++) {
    const link = document.getElementById(links[i]);
    if (link && link.tagName.toLowerCase() === "link") {
      must(link.parentNode, "stylesheet link parent").removeChild(link);
    }
  }
}

// spec is {url:<url to css>, id:<id for link element>, [media:<css link media attr>]}
export function addStylesheetLink(
  document: Document,
  spec: { url: string; id: string; media?: string },
): boolean {
  const head = document.getElementsByTagName("head")[0];
  if (head == null) {
    return false;
  }
  const link = document.createElement("link");
  link.id = spec.id;
  link.rel = "stylesheet";
  link.type = "text/css";
  link.href = spec.url;
  if (spec.media) {
    link.media = spec.media;
  }
  head.appendChild(link);
  return true;
}

/*
 * styles is in the form of json and is parsed with the style utils.
 */
export function createStylesheet(
  document: Document,
  id: string,
  jss: Parameters<Jss["toCSS"]>[0],
): HTMLStyleElement {
  const style = createStyleElement(document, id);
  // JssParser does not use `this`; each call builds a new parser.
  const css = JssUtils().toCSS(jss);

  if (style.styleSheet) {
    style.styleSheet.cssText = css; // Support for IE
  } else {
    style.appendChild(document.createTextNode(css)); // Support for the rest
  }

  return style;
}
